import { faBell } from '@fortawesome/pro-regular-svg-icons';
import { Indicator, SimpleGrid } from '@mantine/core';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ENotificationLevel } from '@feathr/blackbox';
import {
  Button,
  Icon,
  Label,
  NavItemHeader,
  NotificationV2,
  Popover,
  showError,
  Spinner,
  Tab,
  Tabs,
} from '@feathr/components';
import type { IUnifiedNotification } from '@feathr/extender/hooks';
import { useUserNotifications } from '@feathr/extender/hooks';
import { cssVar, moment } from '@feathr/hooks';

import * as styles from './NotificationsPopover.css';

interface INotificationsPopoverProps {
  isMinimized?: boolean;
}

function NotificationsPopover({ isMinimized }: Readonly<INotificationsPopoverProps>): JSX.Element {
  const { t } = useTranslation();
  const { notifications, isLoading, readAll } = useUserNotifications();
  const [isReadingAll, setIsReadingAll] = useState(false);
  const [activeTab, setActiveTab] = useState<'priority' | 'unread' | 'all' | undefined>(
    notifications.some((n) =>
      [ENotificationLevel.warning, ENotificationLevel.critical].includes(n.level),
    )
      ? 'priority'
      : undefined,
  );

  useEffect(() => {
    if (
      notifications.some(
        (n) =>
          [ENotificationLevel.warning, ENotificationLevel.critical].includes(n.level) && !n.isRead,
      )
    ) {
      setActiveTab('priority');
    } else if (notifications.some((n) => !n.isRead)) {
      setActiveTab('unread');
    } else {
      setActiveTab('all');
    }
  }, [isLoading, notifications]);

  function handleClickPriorityTab(): void {
    setActiveTab('priority');
  }

  function handleClickAllTab(): void {
    setActiveTab('all');
  }

  function handleClickUnreadTab(): void {
    setActiveTab('unread');
  }

  async function handleReadAll(): Promise<void> {
    try {
      setIsReadingAll(true);
      await readAll();
    } catch (error) {
      showError(error, t);
    } finally {
      setIsReadingAll(false);
    }
  }

  const unreadCount = notifications.filter((n) => !n.isRead).length;

  function filterNotifications(): JSX.Element | JSX.Element[] {
    let filtered = notifications;
    let message: string | undefined;

    if (activeTab === 'all') {
      message = filtered.length > 0 ? undefined : t('No notifications');
    } else if (activeTab === 'unread') {
      filtered = notifications.filter((n) => !n.isRead);
      message = filtered.length > 0 ? undefined : t('No unread notifications');
    } else if (activeTab === 'priority') {
      filtered = notifications.filter((n) =>
        [ENotificationLevel.warning, ENotificationLevel.critical].includes(n.level),
      );
      message = filtered.length > 0 ? undefined : t('No priority notifications');
    }

    const { today, recent, older } = filtered.reduce(
      (acc, n) => {
        // Last 24 hours
        if (moment(n.timestamp).isAfter(moment().subtract(24, 'hours'))) {
          acc.today.push(n);
        }
        // Last 7 days
        else if (moment(n.timestamp).isAfter(moment().subtract(1, 'week'))) {
          acc.recent.push(n);
        }
        // Older than 7 days
        else {
          acc.older.push(n);
        }

        return acc;
      },
      {
        today: [],
        recent: [],
        older: [],
      } as Record<'today' | 'recent' | 'older', IUnifiedNotification[]>,
    );

    return message ? (
      <div className={styles.empty}>{message}</div>
    ) : (
      <>
        {today.length > 0 && (
          <>
            <Label className={styles.sectionLabel}>{t('Today')}</Label>
            {today.map((n) => (
              <NotificationV2 key={n.id} {...n} />
            ))}
          </>
        )}
        {recent.length > 0 && (
          <>
            <Label className={styles.sectionLabel}>{t('Last 7 days')}</Label>
            {recent.map((n) => (
              <NotificationV2 key={n.id} {...n} />
            ))}
          </>
        )}
        {older.length > 0 && (
          <>
            {/* TODO: Make this section label "Older" when infinite scroll is implemented. */}
            <Label className={styles.sectionLabel}>{t('Last 3 months')}</Label>
            {older.map((n) => (
              <NotificationV2 key={n.id} {...n} />
            ))}
          </>
        )}
      </>
    );
  }

  return (
    <Popover
      arrow={false}
      className={styles.root}
      closeOnBlur={false}
      position={'right'}
      toggle={'onClick'}
    >
      <NavItemHeader
        className={classNames({ [styles.unread]: unreadCount > 0 })}
        prefix={
          <Indicator
            className={styles.navIndicator}
            color={cssVar('--color-badge')}
            disabled={unreadCount === 0}
            label={unreadCount > 99 ? '99+' : unreadCount}
            offset={5}
            processing={notifications.some(
              (n) =>
                [ENotificationLevel.warning, ENotificationLevel.critical].includes(n.level) &&
                n.isRead === false,
            )}
          >
            <Icon className={styles.icon} icon={faBell} />
          </Indicator>
        }
      >
        {!isMinimized && t('Notifications')}
      </NavItemHeader>

      <div className={styles.head}>
        <span className={styles.header}>
          <Label className={styles.label}>
            {unreadCount > 0 ? t('Inbox ({{count}})', { count: unreadCount }) : t('Inbox')}
          </Label>
          {unreadCount > 0 && (
            <Button className={styles.readAll} onClick={handleReadAll} type={'link'}>
              {t('Mark all as read')}
            </Button>
          )}
        </span>
        <Tabs className={styles.tabs}>
          <Tab
            active={activeTab === 'priority'}
            onClick={handleClickPriorityTab}
            title={t('Priority')}
          />
          <Tab active={activeTab === 'all'} onClick={handleClickAllTab} title={t('All')} />
          <Tab active={activeTab === 'unread'} onClick={handleClickUnreadTab} title={t('Unread')} />
        </Tabs>
      </div>
      <div className={styles.scrollableContent}>
        <SimpleGrid className={styles.list} cols={1} verticalSpacing={0}>
          {isLoading || isReadingAll ? <Spinner /> : filterNotifications()}
        </SimpleGrid>
      </div>
    </Popover>
  );
}

export default observer(NotificationsPopover);
